import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Globe,
  ShieldCheck,
  KeyRound,
  Copy,
  CheckCircle,
  AlertTriangle,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react'
import { isAxiosError } from 'axios'
import {
  getIdentityConfig,
  setIdentityConfig,
  startDomainVerification,
  verifyDomain,
  setRequireSso,
  addBreakGlassAdmin,
  issueScimToken,
  setGroupMappings,
} from '../../services/api'
import type { IdentityConfig } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import PageHeader from '../../components/ui/PageHeader'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function errorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { error?: string; message?: string } | undefined
    return data?.error || data?.message || err.message || fallback
  }
  if (err instanceof Error) return err.message
  return fallback
}

const IDENTITY_QUERY_KEY = (orgId: string) => ['identity-config', orgId] as const

const ROLE_OPTIONS = [
  'Org Admin',
  'Policy Analyst',
  'Program Lead',
  'Contributor',
  'Viewer',
  'No access',
] as const

// SCIM base URL derived from the deployed API endpoint (informational only).
function scimBaseUrl(orgId: string): string {
  const cfg = typeof window !== 'undefined' ? window.INNOVATEFLOW_CONFIG : undefined
  const endpoint = cfg?.API_ENDPOINT?.replace(/\/+$/, '') || ''
  return endpoint ? `${endpoint}/scim/v2/${orgId}` : `/scim/v2/${orgId}`
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function IdentitySSO() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const orgId = user?.active_org_id || user?.org_memberships?.[0]?.org_id
  const activeMembership =
    user?.org_memberships?.find((m) => m.org_id === orgId) || user?.org_memberships?.[0]
  const orgName = activeMembership?.org_name
  const isOrgAdmin =
    activeMembership?.role === 'admin' ||
    activeMembership?.role === 'owner' ||
    (user?.groups?.includes('innovateflow-admin') ?? false)

  const [toast, setToast] = useState<{ kind: 'info' | 'success' | 'error'; text: string } | null>(
    null,
  )

  // Auto-dismiss toasts after a few seconds.
  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 6000)
    return () => clearTimeout(t)
  }, [toast])

  const {
    data: cfg,
    isLoading,
    isError,
  } = useQuery({
    queryKey: IDENTITY_QUERY_KEY(orgId as string),
    queryFn: () => getIdentityConfig(orgId as string),
    enabled: !!orgId && isOrgAdmin,
  })

  // ---- Access gate -------------------------------------------------------
  if (!isOrgAdmin || !orgId) {
    return (
      <div className="flex items-center justify-center py-24 px-4">
        <div className="light-card p-8 max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-amber-500/20 text-amber-700 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={26} />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            Organisation admin access required
          </h2>
          <p className="text-sm text-gray-500">
            You need to be an owner or admin of this organisation to manage identity and single
            sign-on.
          </p>
        </div>
      </div>
    )
  }

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: IDENTITY_QUERY_KEY(orgId) })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Identity & SSO"
        description={`Let members sign in with your identity provider. Configure your IdP, map its attributes to InnovateFlow roles, verify your domain, then test before you enforce.${
          orgName ? ` · ${orgName}` : ''
        }`}
        helpKey="org-identity"
      />

      {/* Toast */}
      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}

      {/* Error banner */}
      {isError && (
        <div className="light-card p-4 flex items-center gap-3 border border-red-200 bg-red-50">
          <AlertTriangle size={18} className="text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700">
            Failed to load identity configuration. Please refresh the page or try again shortly.
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="light-card p-4 lg:p-6">
          <LoadingSkeleton rows={4} type="list" />
        </div>
      ) : (
        <>
          {/* (1) Status banner */}
          <StatusBanner cfg={cfg} />

          {/* (2) 5-step progress — Verify domain → Configure IdP → Map attributes →
              Test → Enforce (Fig57). Every checkmark is computed from real, saved
              config; "Test" never shows a fabricated pass since there is no
              backend-recorded connection-test result yet (see ConnectionTestCard). */}
          <IdentityStepper cfg={cfg} />

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 items-start">
            {/* Main column: protocol + IdP details, then attribute/role mapping */}
            <div className="space-y-6">
              <EntraIdpCard orgId={orgId} cfg={cfg} onChanged={invalidate} setToast={setToast} />
              <GroupMappingCard orgId={orgId} cfg={cfg} onChanged={invalidate} setToast={setToast} />
            </div>

            {/* Right rail: SP details, connection test, domain verification */}
            <div className="space-y-6">
              <SpDetailsCard orgName={orgName} />
              <ConnectionTestCard cfg={cfg} setToast={setToast} />
              <DomainVerificationCard
                orgId={orgId}
                cfg={cfg}
                onChanged={invalidate}
                setToast={setToast}
              />
            </div>
          </div>

          {/* (3) SCIM provisioning */}
          <ScimCard orgId={orgId} setToast={setToast} />

          {/* (4) Sign-in policy — includes the "Enforce" step's Require-SSO toggle */}
          <PolicyCard orgId={orgId} cfg={cfg} onChanged={invalidate} setToast={setToast} />
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// (1b) 5-step progress stepper (Fig57) — generic across SAML/OIDC, computed
// entirely from real saved config so a checkmark always means "actually true".
// ---------------------------------------------------------------------------

function IdentityStepper({ cfg }: { cfg: IdentityConfig | undefined }) {
  const steps = [
    { label: 'Verify domain', done: (cfg?.verified_domains?.length ?? 0) > 0 },
    { label: 'Configure IdP', done: !!cfg?.idp_type },
    { label: 'Map attributes', done: (cfg?.group_mappings?.length ?? 0) > 0 },
    // No backend-recorded connection-test result exists yet, so "Test" is never
    // marked done — that would be a fabricated pass. It's shown as the current
    // step once the config ahead of it is real.
    { label: 'Test', done: false },
    { label: 'Enforce', done: !!cfg?.require_sso },
  ]
  const firstNotDone = steps.findIndex((s) => !s.done)
  const currentIdx = firstNotDone === -1 ? steps.length - 1 : firstNotDone

  return (
    <div className="light-card p-4 lg:p-6 overflow-x-auto">
      <ol className="flex items-center gap-1 min-w-max">
        {steps.map((step, idx) => (
          <li key={step.label} className="flex items-center">
            <div className="flex items-center gap-2">
              <span
                className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                  step.done
                    ? 'bg-green-500/20 text-green-700'
                    : idx === currentIdx
                      ? 'bg-iflow-600 text-white'
                      : 'bg-gray-100 text-gray-400'
                }`}
              >
                {step.done ? <CheckCircle size={15} /> : idx + 1}
              </span>
              <span
                className={`text-sm font-semibold whitespace-nowrap ${
                  step.done || idx === currentIdx ? 'text-gray-800' : 'text-gray-400'
                }`}
              >
                {step.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <span className="mx-3 h-px w-10 bg-iflow-100 flex-shrink-0" aria-hidden />
            )}
          </li>
        ))}
      </ol>
    </div>
  )
}

// ---------------------------------------------------------------------------
// (2b) Service Provider details — Fig57 right rail. There is NO live per-org
// SAML federation endpoint today (verified against terraform/ + identity/
// handler.py: only a generic Cognito Hosted-UI domain exists, no
// aws_cognito_identity_provider is ever created, and the handler's own
// docstring says IdP wiring is external). Rendering a plausible-looking ACS
// URL / SP Entity ID here would be well-formed but WRONG — it would send an
// org admin to configure their real IdP against an endpoint Cognito can't
// consume. Show the honest "not provisioned" state instead of fabricating it.
// ---------------------------------------------------------------------------

function SpDetailsCard({ orgName }: { orgName?: string }) {
  return (
    <div className="light-card p-4 lg:p-6 space-y-3">
      <div className="flex items-center gap-2">
        <Globe size={18} className="text-iflow-600" />
        <h2 className="font-bold text-gray-800">Service provider details</h2>
      </div>
      <p className="text-sm text-gray-500">
        Copy-in values for your IdP application (ACS/Reply URL, SP Entity ID) are issued once
        SecureEmbed provisions a live federation endpoint for {orgName || 'your organisation'}.
      </p>
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        Not yet provisioned. The configuration you save on the left is stored and validated now;
        live sign-in federation into this identity provider is a separate platform rollout still
        in progress.
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// (3b) Connection test — Fig57 right rail. Honest: no backend endpoint records
// a real test result (identity/handler.py has no test-connection route), so
// this never shows a fabricated "passed" badge — it explains what's real.
// ---------------------------------------------------------------------------

function ConnectionTestCard({
  cfg,
  setToast,
}: {
  cfg: IdentityConfig | undefined
  setToast: (t: { kind: 'info' | 'success' | 'error'; text: string }) => void
}) {
  const configured = !!cfg?.idp_type
  return (
    <div className="light-card p-4 lg:p-6 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} className="text-iflow-600" />
          <h2 className="font-bold text-gray-800">Connection test</h2>
        </div>
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
          Not yet tested
        </span>
      </div>
      <p className="text-sm text-gray-500">
        {configured
          ? 'Verify a real sign-in flows through your IdP before you enforce it for everyone.'
          : 'Configure your identity provider on the left before testing.'}
      </p>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() =>
            setToast({
              kind: 'info',
              text: configured
                ? 'Test sign-in requires a completed external sign-in through your IdP — this is not yet automated on SecureEmbed.'
                : 'Configure your identity provider first.',
            })
          }
          className="inline-flex items-center gap-2 bg-white text-iflow-700 border border-iflow-200 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-iflow-50"
        >
          <RefreshCw size={14} /> Run test sign-in
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------

function Toast({
  toast,
  onClose,
}: {
  toast: { kind: 'info' | 'success' | 'error'; text: string }
  onClose: () => void
}) {
  const styles =
    toast.kind === 'success'
      ? 'bg-green-50 border-green-200 text-green-800'
      : toast.kind === 'error'
        ? 'bg-red-50 border-red-200 text-red-700'
        : 'bg-iflow-50 border-iflow-200 text-iflow-900'
  const Icon =
    toast.kind === 'success' ? CheckCircle : toast.kind === 'error' ? AlertTriangle : ShieldCheck
  return (
    <div className={`light-card p-3 flex items-center gap-3 border ${styles}`}>
      <Icon size={18} className="flex-shrink-0" />
      <p className="text-sm flex-1">{toast.text}</p>
      <button
        type="button"
        onClick={onClose}
        aria-label="Dismiss"
        className="text-gray-500 hover:text-gray-600 p-1 rounded-lg hover:bg-black/5"
      >
        <X size={16} />
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Copyable field
// ---------------------------------------------------------------------------

function CopyField({
  value,
  label,
  mono = true,
}: {
  value: string
  label?: string
  mono?: boolean
}) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      setCopied(false)
    }
  }
  return (
    <div>
      {label && <p className="text-xs font-semibold text-gray-600 mb-1">{label}</p>}
      <div className="flex items-stretch gap-2">
        <code
          className={`flex-1 min-w-0 truncate rounded-lg border border-iflow-100 bg-iflow-50/60 px-3 py-2 text-xs text-iflow-900 ${
            mono ? 'font-mono' : ''
          }`}
          title={value}
        >
          {value}
        </code>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1 bg-white text-iflow-700 border border-iflow-200 px-3 py-2 rounded-lg text-xs font-medium hover:bg-iflow-50 flex-shrink-0"
        >
          {copied ? <CheckCircle size={14} /> : <Copy size={14} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// (1) Status banner
// ---------------------------------------------------------------------------

function StatusBanner({ cfg }: { cfg: IdentityConfig | undefined }) {
  const idp = cfg?.idp_type ? cfg.idp_type.toUpperCase() : 'Not configured'
  const status = cfg?.status || 'unconfigured'
  const connected = !!cfg?.idp_type && (cfg?.verified_domains?.length ?? 0) > 0
  return (
    <div className="light-card p-4 lg:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-iflow-100 text-iflow-700 flex items-center justify-center flex-shrink-0">
            <ShieldCheck size={22} />
          </div>
          <div>
            <p className="font-bold text-gray-800">Microsoft Entra ID federation</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Protocol: {idp} · Status: {status}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
              connected ? 'bg-green-500/20 text-green-700' : 'bg-gray-500/20 text-gray-600'
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-gray-400'}`}
            />
            {connected ? 'Connected' : 'Not connected'}
          </span>
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
              cfg?.require_sso
                ? 'bg-iflow-100 text-iflow-700'
                : 'bg-amber-500/20 text-amber-700'
            }`}
          >
            {cfg?.require_sso ? 'SSO required' : 'SSO optional'}
          </span>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// (2) Domain verification
// ---------------------------------------------------------------------------

function DomainVerificationCard({
  orgId,
  cfg,
  onChanged,
  setToast,
}: {
  orgId: string
  cfg: IdentityConfig | undefined
  onChanged: () => void
  setToast: (t: { kind: 'info' | 'success' | 'error'; text: string }) => void
}) {
  const [domain, setDomain] = useState('')
  const [txt, setTxt] = useState<{ txt_name: string; txt_value: string; domain: string } | null>(
    null,
  )

  const start = useMutation({
    mutationFn: (d: string) => startDomainVerification(orgId, d),
    onSuccess: (data) => {
      setTxt(data)
      onChanged()
    },
    onError: (err) =>
      setToast({ kind: 'error', text: errorMessage(err, 'Failed to start domain verification.') }),
  })

  const verify = useMutation({
    mutationFn: (d: string) => verifyDomain(orgId, d),
    onSuccess: (data) => {
      if (data.status === 'verified') {
        setToast({ kind: 'success', text: `Domain ${txt?.domain ?? ''} verified.` })
        setTxt(null)
        setDomain('')
      } else if (
        data.status === 'verification_pending' ||
        data.status === 'dns_lookup_unavailable'
      ) {
        setToast({
          kind: 'info',
          text:
            data.reason ||
            (data.status === 'dns_lookup_unavailable'
              ? 'DNS lookup is temporarily unavailable. The TXT record may not have propagated yet — try again shortly.'
              : 'Verification pending — the TXT record has not propagated yet. This can take up to an hour.'),
        })
      } else {
        setToast({
          kind: 'error',
          text: data.reason || `Verification ${data.status}.`,
        })
      }
      onChanged()
    },
    onError: (err) =>
      setToast({ kind: 'error', text: errorMessage(err, 'Failed to verify domain.') }),
  })

  const domainValid = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i.test(
    domain.trim(),
  )
  const verifiedDomains = cfg?.verified_domains ?? []
  const pendingDomains = cfg?.pending_domains ?? {}

  return (
    <div className="light-card p-4 lg:p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Globe size={18} className="text-iflow-600" />
        <h2 className="font-bold text-gray-800">Domain verification</h2>
      </div>
      <p className="text-sm text-gray-500">
        Prove you own the domains your members sign in with. Add the TXT record below at your DNS
        host, then click Verify.
      </p>

      {/* Verified chips */}
      {verifiedDomains.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {verifiedDomains.map((d) => (
            <span
              key={d}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-700"
            >
              <CheckCircle size={13} /> {d}
            </span>
          ))}
        </div>
      )}

      {/* Pending chips (from server state) */}
      {Object.keys(pendingDomains).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.keys(pendingDomains).map((d) => (
            <span
              key={d}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-700"
            >
              <RefreshCw size={13} /> {d} · pending
            </span>
          ))}
        </div>
      )}

      {/* Add domain */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="domain-input">
            Domain
          </label>
          <input
            id="domain-input"
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="example.gov.au"
            className="w-full border border-iflow-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-iflow-500"
          />
        </div>
        <button
          type="button"
          onClick={() => domainValid && start.mutate(domain.trim().toLowerCase())}
          disabled={!domainValid || start.isPending}
          className="inline-flex items-center gap-2 bg-iflow-700 hover:bg-iflow-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus size={16} /> {start.isPending ? 'Generating…' : 'Add domain'}
        </button>
      </div>
      {domain && !domainValid && (
        <p className="text-xs text-red-600">Enter a valid domain name (e.g. example.gov.au).</p>
      )}

      {/* TXT record to publish */}
      {txt && (
        <div className="rounded-lg border border-iflow-100 bg-iflow-50/40 p-4 space-y-3">
          <p className="text-sm font-semibold text-iflow-900">
            Publish this TXT record for{' '}
            <span className="font-mono">{txt.domain}</span>, then click Verify:
          </p>
          <CopyField label="Record name (host)" value={txt.txt_name} />
          <CopyField label="Record value" value={txt.txt_value} />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => verify.mutate(txt.domain)}
              disabled={verify.isPending}
              className="inline-flex items-center gap-2 bg-iflow-700 hover:bg-iflow-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ShieldCheck size={16} /> {verify.isPending ? 'Verifying…' : 'Verify'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// (3) Entra IdP
// ---------------------------------------------------------------------------

function EntraIdpCard({
  orgId,
  cfg,
  onChanged,
  setToast,
}: {
  orgId: string
  cfg: IdentityConfig | undefined
  onChanged: () => void
  setToast: (t: { kind: 'info' | 'success' | 'error'; text: string }) => void
}) {
  const [idpType, setIdpType] = useState<'saml' | 'oidc'>(cfg?.idp_type ?? 'saml')
  const [metadataMode, setMetadataMode] = useState<'url' | 'xml'>('url')
  const [metadataUrl, setMetadataUrl] = useState('')
  const [metadataXml, setMetadataXml] = useState('')

  // Re-sync the protocol select when config loads/changes.
  useEffect(() => {
    if (cfg?.idp_type) setIdpType(cfg.idp_type)
  }, [cfg?.idp_type])

  const save = useMutation({
    mutationFn: () =>
      setIdentityConfig(orgId, {
        idp_type: idpType,
        ...(metadataMode === 'url'
          ? { metadata_url: metadataUrl.trim() }
          : { metadata_xml: metadataXml.trim() }),
      }),
    onSuccess: () => {
      setToast({ kind: 'success', text: 'Identity provider details saved.' })
      onChanged()
    },
    onError: (err) =>
      setToast({ kind: 'error', text: errorMessage(err, 'Failed to save IdP details.') }),
  })

  const metadataProvided =
    metadataMode === 'url' ? metadataUrl.trim().length > 0 : metadataXml.trim().length > 0

  return (
    <div className="light-card p-4 lg:p-6 space-y-4">
      <div className="flex items-center gap-2">
        <ShieldCheck size={18} className="text-iflow-600" />
        <h2 className="font-bold text-gray-800">Identity provider details</h2>
      </div>

      <div>
        <label className="block text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">
          Protocol
        </label>
        <div className="flex gap-2" role="group" aria-label="Federation protocol">
          <button
            type="button"
            onClick={() => setIdpType('saml')}
            aria-pressed={idpType === 'saml'}
            className={`px-4 py-2 rounded-lg text-sm font-medium border ${
              idpType === 'saml'
                ? 'bg-iflow-700 text-white border-iflow-500'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            SAML 2.0
          </button>
          <button
            type="button"
            onClick={() => setIdpType('oidc')}
            aria-pressed={idpType === 'oidc'}
            className={`px-4 py-2 rounded-lg text-sm font-medium border ${
              idpType === 'oidc'
                ? 'bg-iflow-700 text-white border-iflow-500'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            OIDC
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {idpType === 'saml'
            ? 'Recommended for Microsoft Entra ID.'
            : 'OpenID Connect — recommended for Okta / Google Workspace.'}
        </p>
      </div>

      {/* Metadata mode toggle */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setMetadataMode('url')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
            metadataMode === 'url'
              ? 'bg-iflow-700 text-white border-iflow-500'
              : 'bg-white text-iflow-700 border-iflow-200 hover:bg-iflow-50'
          }`}
        >
          Metadata URL
        </button>
        <button
          type="button"
          onClick={() => setMetadataMode('xml')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
            metadataMode === 'xml'
              ? 'bg-iflow-700 text-white border-iflow-500'
              : 'bg-white text-iflow-700 border-iflow-200 hover:bg-iflow-50'
          }`}
        >
          Paste metadata XML
        </button>
      </div>

      {metadataMode === 'url' ? (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="metadata-url">
            Entra metadata URL
          </label>
          <input
            id="metadata-url"
            type="url"
            value={metadataUrl}
            onChange={(e) => setMetadataUrl(e.target.value)}
            placeholder="https://login.microsoftonline.com/<tenant>/federationmetadata/2007-06/federationmetadata.xml"
            className="w-full border border-iflow-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-iflow-500"
          />
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="metadata-xml">
            Federation metadata XML
          </label>
          <textarea
            id="metadata-xml"
            value={metadataXml}
            onChange={(e) => setMetadataXml(e.target.value)}
            rows={6}
            placeholder="<EntityDescriptor xmlns=…>…</EntityDescriptor>"
            className="w-full border border-iflow-200 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-iflow-500"
          />
        </div>
      )}

      <p className="text-xs text-gray-500">
        This is stored and validated now. See "Service provider details" on the right for what's
        needed to actually enable sign-in with your IdP.
      </p>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={!metadataProvided || save.isPending}
          className="inline-flex items-center gap-2 bg-iflow-700 hover:bg-iflow-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {save.isPending ? 'Saving…' : 'Save IdP details'}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// (4) Map attributes — Entra/IdP groups → InnovateFlow roles. This is the real,
// working mechanism behind Fig57's generic "Map attributes" step: SAML/OIDC
// assertions carry group claims, and those claims are what's mapped to a role
// here. (There is no backend support for renaming the underlying claim keys
// themselves — mail/displayName/groups are fixed — so that part of the mockup
// isn't rendered as an editable table; it would save nothing real.)
// ---------------------------------------------------------------------------

type MappingRow = { group: string; role: string; resource_group?: string }

function GroupMappingCard({
  orgId,
  cfg,
  onChanged,
  setToast,
}: {
  orgId: string
  cfg: IdentityConfig | undefined
  onChanged: () => void
  setToast: (t: { kind: 'info' | 'success' | 'error'; text: string }) => void
}) {
  const initial = useMemo<MappingRow[]>(
    () => (cfg?.group_mappings ?? []).map((m) => ({ ...m })),
    [cfg?.group_mappings],
  )
  const [rows, setRows] = useState<MappingRow[]>(initial)
  const [dirty, setDirty] = useState(false)

  // Reset local rows when server state changes and we have no pending edits.
  useEffect(() => {
    if (!dirty) setRows(initial)
  }, [initial, dirty])

  const update = (idx: number, patch: Partial<MappingRow>) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
    setDirty(true)
  }
  const move = (idx: number, dir: -1 | 1) => {
    setRows((prev) => {
      const next = [...prev]
      const target = idx + dir
      if (target < 0 || target >= next.length) return prev
      ;[next[idx], next[target]] = [next[target], next[idx]]
      return next
    })
    setDirty(true)
  }
  const remove = (idx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== idx))
    setDirty(true)
  }
  const add = () => {
    setRows((prev) => [...prev, { group: '', role: ROLE_OPTIONS[0], resource_group: '' }])
    setDirty(true)
  }

  const save = useMutation({
    mutationFn: () =>
      setGroupMappings(
        orgId,
        rows
          .filter((r) => r.group.trim())
          .map((r) => ({
            group: r.group.trim(),
            role: r.role,
            ...(r.resource_group?.trim()
              ? { resource_group: r.resource_group.trim() }
              : {}),
          })),
      ),
    onSuccess: () => {
      setToast({ kind: 'success', text: 'Group mappings saved.' })
      setDirty(false)
      onChanged()
    },
    onError: (err) =>
      setToast({ kind: 'error', text: errorMessage(err, 'Failed to save group mappings.') }),
  })

  return (
    <div className="light-card p-4 lg:p-6 space-y-4">
      <div className="flex items-center gap-2">
        <ShieldCheck size={18} className="text-iflow-600" />
        <h2 className="font-bold text-gray-800">Map attributes — group → role</h2>
      </div>
      <p className="text-sm text-gray-500">
        Your IdP's <code className="text-xs bg-iflow-50 px-1 py-0.5 rounded">groups</code> claim
        drives role assignment — map each security/directory group your assertions carry to an
        InnovateFlow role. Evaluated top-down; the first matching group wins. Use the arrows to set
        priority.
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-iflow-100">
              <th className="px-2 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 w-10">
                #
              </th>
              <th className="px-2 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                IdP group / claim value
              </th>
              <th className="px-2 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Role
              </th>
              <th className="px-2 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Resource group (optional)
              </th>
              <th className="px-2 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 w-24" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-2 py-6 text-center text-sm text-gray-500">
                  No mappings yet. Add a row to map an Entra group to a role.
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr key={idx} className="border-b border-gray-100 last:border-b-0 align-middle">
                  <td className="px-2 py-2 text-sm text-gray-500">{idx + 1}</td>
                  <td className="px-2 py-2">
                    <input
                      type="text"
                      value={row.group}
                      onChange={(e) => update(idx, { group: e.target.value })}
                      placeholder="SG-Policy-Analysts"
                      className="w-full border border-iflow-200 rounded-lg px-2.5 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-iflow-500"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <select
                      value={row.role}
                      onChange={(e) => update(idx, { role: e.target.value })}
                      className="w-full border border-iflow-200 rounded-lg px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-iflow-500"
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="text"
                      value={row.resource_group ?? ''}
                      onChange={(e) => update(idx, { resource_group: e.target.value })}
                      placeholder="(all)"
                      className="w-full border border-iflow-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-iflow-500"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        aria-label="Move up"
                        title="Move up"
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0}
                        className="text-gray-500 hover:text-iflow-700 p-1 rounded hover:bg-iflow-50 disabled:opacity-30 disabled:hover:bg-transparent"
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        aria-label="Move down"
                        title="Move down"
                        onClick={() => move(idx, 1)}
                        disabled={idx === rows.length - 1}
                        className="text-gray-500 hover:text-iflow-700 p-1 rounded hover:bg-iflow-50 disabled:opacity-30 disabled:hover:bg-transparent"
                      >
                        ▼
                      </button>
                      <button
                        type="button"
                        aria-label="Remove row"
                        title="Remove row"
                        onClick={() => remove(idx)}
                        className="text-gray-500 hover:text-red-600 p-1 rounded hover:bg-red-50"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-2 bg-white text-iflow-700 border border-iflow-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-iflow-50"
        >
          <Plus size={16} /> Add mapping
        </button>
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending || !dirty}
          className="inline-flex items-center gap-2 bg-iflow-700 hover:bg-iflow-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {save.isPending ? 'Saving…' : 'Save mappings'}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// (5) SCIM provisioning
// ---------------------------------------------------------------------------

function ScimCard({
  orgId,
  setToast,
}: {
  orgId: string
  setToast: (t: { kind: 'info' | 'success' | 'error'; text: string }) => void
}) {
  const [token, setToken] = useState<{ scim_token: string; warning: string; rotated: boolean } | null>(
    null,
  )
  const [confirmOpen, setConfirmOpen] = useState(false)

  // P7 honesty: issue failures are surfaced by ConfirmDialog (onConfirm throws →
  // the dialog stays open and shows the real error). Success closes and toasts.
  const issue = useMutation({
    mutationFn: () => issueScimToken(orgId),
    onSuccess: (data) => {
      setToken(data)
      setConfirmOpen(false)
      setToast({
        kind: 'success',
        text: data.rotated
          ? 'SCIM token rotated. Copy it now — it is shown only once.'
          : 'SCIM token generated. Copy it now — it is shown only once.',
      })
    },
  })

  return (
    <div className="light-card p-4 lg:p-6 space-y-4">
      <div className="flex items-center gap-2">
        <KeyRound size={18} className="text-iflow-600" />
        <h2 className="font-bold text-gray-800">SCIM provisioning</h2>
      </div>
      <p className="text-sm text-gray-500">
        Entra creates, updates and deactivates members automatically. Deactivation reclaims the seat
        instantly. Configure Entra with the base URL below and a bearer token issued here.
      </p>

      <CopyField label="SCIM base URL" value={scimBaseUrl(orgId)} />

      {/* Issued token — shown once */}
      {token && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">{token.warning}</p>
          </div>
          <CopyField label="Bearer token (copy now — not retrievable again)" value={token.scim_token} />
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={issue.isPending}
          className="inline-flex items-center gap-2 bg-iflow-700 hover:bg-iflow-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw size={16} /> {issue.isPending ? 'Issuing…' : 'Generate / Rotate SCIM token'}
        </button>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Generate a new SCIM token?"
        message="This issues a fresh bearer token and immediately invalidates any existing token. Entra provisioning will stop until you update it with the new token. The token is shown only once."
        confirmLabel="Generate token"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={async () => {
          try {
            await issue.mutateAsync()
          } catch (err) {
            throw new Error(errorMessage(err, 'Failed to issue SCIM token.'))
          }
        }}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// (6) Sign-in policy
// ---------------------------------------------------------------------------

function PolicyCard({
  orgId,
  cfg,
  onChanged,
  setToast,
}: {
  orgId: string
  cfg: IdentityConfig | undefined
  onChanged: () => void
  setToast: (t: { kind: 'info' | 'success' | 'error'; text: string }) => void
}) {
  const [breakGlassInput, setBreakGlassInput] = useState('')

  const requireSso = useMutation({
    mutationFn: (enabled: boolean) => setRequireSso(orgId, enabled),
    onSuccess: (_data, enabled) => {
      setToast({
        kind: 'success',
        text: enabled ? 'Require-SSO enabled.' : 'Require-SSO disabled.',
      })
      onChanged()
    },
    onError: (err) => {
      // 409 → no break-glass admin nominated yet.
      if (isAxiosError(err) && err.response?.status === 409) {
        const serverMsg = (err.response?.data as { error?: string } | undefined)?.error
        setToast({
          kind: 'error',
          text: serverMsg || 'Nominate a break-glass admin first',
        })
      } else {
        setToast({ kind: 'error', text: errorMessage(err, 'Failed to update require-SSO.') })
      }
    },
  })

  const addBreakGlass = useMutation({
    mutationFn: (identity: string) => addBreakGlassAdmin(orgId, identity),
    onSuccess: () => {
      setToast({ kind: 'success', text: 'Break-glass admin added.' })
      setBreakGlassInput('')
      onChanged()
    },
    onError: (err) =>
      setToast({ kind: 'error', text: errorMessage(err, 'Failed to add break-glass admin.') }),
  })

  const updateToggle = useMutation({
    mutationFn: (patch: { jit_licensing?: boolean; idle_seat_reclaim?: boolean }) =>
      setIdentityConfig(orgId, patch),
    onSuccess: (_data, patch) => {
      const label = 'jit_licensing' in patch ? 'Just-in-time licensing' : 'Idle-seat reclaim'
      setToast({ kind: 'success', text: `${label} updated.` })
      onChanged()
    },
    onError: (err) =>
      setToast({ kind: 'error', text: errorMessage(err, 'Failed to update setting.') }),
  })

  const breakGlassAdmins = cfg?.break_glass_admins ?? []
  const breakGlassValid = breakGlassInput.trim().length > 0

  return (
    <div className="light-card p-4 lg:p-6 space-y-5">
      <div className="flex items-center gap-2">
        <ShieldCheck size={18} className="text-iflow-600" />
        <h2 className="font-bold text-gray-800">Sign-in policy</h2>
      </div>

      {/* Require SSO */}
      <PolicyToggleRow
        title="Require SSO for all members"
        description="Password sign-in disabled. Break-glass admins remain exempt so you can never be locked out."
        checked={!!cfg?.require_sso}
        disabled={requireSso.isPending}
        onChange={(next) => requireSso.mutate(next)}
      />

      {/* Break-glass admins */}
      <div className="rounded-lg border border-iflow-100 bg-iflow-50/40 p-4 space-y-3">
        <div>
          <p className="text-sm font-semibold text-gray-800">Break-glass admins</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Exempt from require-SSO. Nominate at least one before enabling require-SSO, or the change
            is rejected.
          </p>
        </div>
        {breakGlassAdmins.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {breakGlassAdmins.map((a) => (
              <span
                key={a}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-iflow-100 text-iflow-700"
              >
                <ShieldCheck size={13} /> {a}
              </span>
            ))}
          </div>
        )}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            type="text"
            value={breakGlassInput}
            onChange={(e) => setBreakGlassInput(e.target.value)}
            placeholder="admin@example.gov.au"
            className="flex-1 border border-iflow-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-iflow-500"
          />
          <button
            type="button"
            onClick={() => breakGlassValid && addBreakGlass.mutate(breakGlassInput.trim())}
            disabled={!breakGlassValid || addBreakGlass.isPending}
            className="inline-flex items-center gap-2 bg-white text-iflow-700 border border-iflow-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-iflow-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={16} /> {addBreakGlass.isPending ? 'Adding…' : 'Add admin'}
          </button>
        </div>
      </div>

      {/* JIT + idle reclaim */}
      <PolicyToggleRow
        title="Just-in-time licensing"
        description="First SSO sign-in claims a seat from the pool per the group mapping above."
        checked={!!cfg?.jit_licensing}
        disabled={updateToggle.isPending}
        onChange={(next) => updateToggle.mutate({ jit_licensing: next })}
      />
      <PolicyToggleRow
        title="Auto-reclaim idle seats"
        description="Seats unused for 60 days return to the pool automatically."
        checked={!!cfg?.idle_seat_reclaim}
        disabled={updateToggle.isPending}
        onChange={(next) => updateToggle.mutate({ idle_seat_reclaim: next })}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Toggle row (light theme)
// ---------------------------------------------------------------------------

function PolicyToggleRow({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string
  description: string
  checked: boolean
  disabled?: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-semibold text-gray-800">{title}</p>
        <p className="text-xs text-gray-500 mt-0.5 max-w-xl">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={title}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          checked ? 'bg-iflow-500' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  )
}
